import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { HttpBackend, HttpClient, HttpParams } from '@angular/common/http';
import { catchError, finalize, map, retry, share, switchMap, take, tap, timeout } from 'rxjs/operators';
import { OAuthToken } from '../../shared/models/OAuthToken';
import { ConfigurationService } from '@ciam/library';


@Injectable({
  providedIn: 'root'
})
export class OauthService {

  static readonly EXPIRE_TOKEN_SECONDS = 3_600;
  private token: OAuthToken;

  private isPending = false;
  private activeTokenReader$ = new Observable<OAuthToken>();

  private http: HttpClient;

  constructor(private backend: HttpBackend,
              private config: ConfigurationService) {
    // bypass interceptors
    this.http = new HttpClient(this.backend);
  }

  /**
   * Adds 'expires-at' value which converts expiration duration to instant
   *
   * @param token - to be rectified
   */
  rectify(token: OAuthToken): OAuthToken {
    if (token == null) return undefined;

    const duration = token.expires_in || OauthService.EXPIRE_TOKEN_SECONDS;
    const instant = (new Date()).getTime() + (duration * 1000);
    return { ...token, ...{ expires_at: instant } };
  }

  /**
   * Determines if the token has expired
   *
   * @param token - to check expiration
   * @return true if the token 'expire_at' is greater than 'now', false otherwise
   */
  isExpired(token: OAuthToken): boolean {
    return (token == null || ((token.expires_at || 0) < (new Date()).getTime()));
  }

  /**
   * @return cached token if it is not expired, otherwise a new token is fetched and cached
   * @see _apiPostToken$
   */
  readOauthToken$(): Observable<OAuthToken> {
    if (this.isExpired(this.token)) {
      if (this.isPending) return this.activeTokenReader$;
      else {
        this.isPending = true;
        this.activeTokenReader$ = this._apiPostToken$().pipe(
          take(1),
          share(),
          tap(token => this.token = token),
          finalize(() => this.isPending = false)
        );
        return this.activeTokenReader$;
      }
    }
    return of(this.token);
  }

  /**
   * Fetch the API gateway token from back-end
   *
   * @return fetched token
   */
  _apiPostToken$(): Observable<OAuthToken> {

    // Source the config via dict$ (not synchronous select) so the token request
    // defers until the app configuration has finished loading. The OauthInterceptor
    // can fire this token request very early (concurrently with config loading);
    // reading config synchronously at that point yields undefined values and
    // builds timeout(undefined), which throws "Cannot destructure property 'first'".
    // dict$ waits for the load to complete, then emits the resolved values once.
const configSource = this.config.dict$({
  host: 'oauth.host',
  url: 'endpoints.oauth.url',
  timeout: 'endpoints.oauth.timeout',
  retry: 'endpoints.oauth.retry',
  grant_type: 'oauth.grant_type',
  client_id: 'oauth.client_id'
}).pipe(take(1));

    const httpSource = (config) => {
      const url = `https://${config.host}${config.url}`;
      const body = new HttpParams()
        .set('grant_type', config.grant_type)
        .set('client_id', config.client_id);

      return this.http.post(url, body).pipe(
        timeout(config.timeout),
        map(this.rectify),
        catchError(err => throwError(err)),
        retry(config.retry),
      );
    };

    return configSource.pipe(switchMap(httpSource));
  }
}
